import { useState } from "preact/hooks";

import {
  CalendarChipButton,
  occurrenceKey,
  resolveChipMeta,
} from "../components/CalendarChipButton";
import {
  buildMonthGrid,
  isSameDay,
  monthLabel,
  toISODate,
  todayLocal,
  weekdayShortLabel,
} from "../lib/calendarDates";
import type { ScheduleOccurrence } from "../types";

const MAX_VISIBLE_CHIPS = 3;
const OVERFLOW_THRESHOLD = 4;

export interface CalendarMonthViewProps {
  anchor: Date;
  occurrences: ScheduleOccurrence[];
  assignmentDateById: Map<string, string>;
  movedFromByAssignmentId: Map<string, boolean>;
  thisWeekDays: Date[];
  scopeLabel: string;
  canWrite: boolean;
  onTapOccurrence: (occurrence: ScheduleOccurrence) => void;
  onAddForDay: (dateIso: string) => void;
}

/**
 * §2.3/§2.12 — desktop month view. A real `<table>` (never the WAI-ARIA
 * `grid` widget pattern — §2.12's own a11y rationale): `<caption
 * class="sr-only">` states the visible month + scope, `<th scope="col">`
 * per weekday, every chip a real `<button>` inside its `<td>` so keyboard
 * Tab order moves cell-by-cell/row-by-row with no bespoke arrow-key
 * handling.
 */
export function CalendarMonthView({
  anchor,
  occurrences,
  assignmentDateById,
  movedFromByAssignmentId,
  thisWeekDays,
  scopeLabel,
  canWrite,
  onTapOccurrence,
  onAddForDay,
}: CalendarMonthViewProps) {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const weeks = buildMonthGrid(anchor);
  const today = todayLocal();

  const byDate = new Map<string, ScheduleOccurrence[]>();
  for (const occ of occurrences) {
    const list = byDate.get(occ.date);
    if (list) list.push(occ);
    else byDate.set(occ.date, [occ]);
  }

  return (
    <table class="calendar-month">
      <caption class="sr-only">{`Calendar, ${monthLabel(anchor)}, ${scopeLabel}`}</caption>
      <thead>
        <tr>
          {weeks[0]?.map((date) => (
            <th key={toISODate(date)} scope="col">
              {weekdayShortLabel(date)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week, weekIndex) => (
          <tr key={weekIndex}>
            {week.map((date) => {
              const iso = toISODate(date);
              const dayOccurrences = byDate.get(iso) ?? [];
              const isToday = isSameDay(date, today);
              const isPast = !isToday && date.getTime() < today.getTime();
              const isOutsideMonth = date.getMonth() !== anchor.getMonth();
              const isExpanded = expandedDays.has(iso);
              const overOverflow = dayOccurrences.length > OVERFLOW_THRESHOLD && !isExpanded;
              const visible = overOverflow
                ? dayOccurrences.slice(0, MAX_VISIBLE_CHIPS)
                : dayOccurrences;
              const hiddenCount = dayOccurrences.length - MAX_VISIBLE_CHIPS;

              const cellClass = [
                "calendar-day",
                isToday ? "is-today" : null,
                isPast ? "is-past" : null,
                isOutsideMonth ? "is-outside-month" : null,
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <td key={iso} class={cellClass}>
                  <div class="calendar-day-number">{date.getDate()}</div>
                  <div class="calendar-day-chips">
                    {visible.map((occ) => {
                      const meta = resolveChipMeta(
                        occ,
                        assignmentDateById,
                        movedFromByAssignmentId,
                        thisWeekDays,
                      );
                      return (
                        <CalendarChipButton
                          key={occurrenceKey(occ)}
                          occurrence={occ}
                          moved={meta.moved}
                          movedTargetLabel={meta.movedTargetLabel}
                          onTap={onTapOccurrence}
                        />
                      );
                    })}
                    {overOverflow ? (
                      <button
                        type="button"
                        class="calendar-day-overflow"
                        onClick={() => setExpandedDays((prev) => new Set(prev).add(iso))}
                      >
                        +{hiddenCount} more
                      </button>
                    ) : null}
                    {canWrite ? (
                      <button
                        type="button"
                        class="calendar-day-add"
                        aria-label={`Add a task for ${iso}`}
                        onClick={() => onAddForDay(iso)}
                      >
                        +
                      </button>
                    ) : null}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
